/**
 * Resolves an image URL from various formats (GCS, data URLs, regular URLs)
 * @param imageUrl - The raw image URL (can be gs://, data:, or regular URL)
 * @returns The resolved URL suitable for use in img src or Next.js Image component
 */
export function resolveImageUrl(imageUrl: string | undefined | null): string {
  if (!imageUrl) return "";

  // Handle data URLs (base64 encoded images)
  if (imageUrl.startsWith("data:")) {
    return imageUrl;
  }

  // Handle Google Cloud Storage URLs (gs://)
  if (imageUrl.startsWith("gs://")) {
    return `https://storage.googleapis.com/${imageUrl.substring(5)}`;
  }

  // Return regular URLs as-is
  return imageUrl;
}

/**
 * Determines if an image should be unoptimized for Next.js Image component
 * @param imageUrl - The image URL to check
 * @returns true if the image should be unoptimized (e.g., for data URLs)
 */
export function shouldUnoptimizeImage(
  imageUrl: string | undefined | null,
): boolean {
  return imageUrl?.startsWith("data:") || false;
}

// Match console/next.config.mjs without broadening the optimizer's remote access.
// Tenant-supplied URLs outside these hosts must continue to render directly.
const OPTIMIZED_IMAGE_HOSTS = new Set([
  "storage.googleapis.com",
  "images.unsplash.com",
  "storage.cloud.google.com",
  "assets.fullscript.io",
]);

/** Whether Next's image optimizer may resize this URL. */
export function canOptimizeImage(src: string): boolean {
  if (src.startsWith("/") && !src.startsWith("//")) return true;
  try {
    const url = new URL(src);
    return (
      url.protocol === "https:" &&
      url.port === "" &&
      OPTIMIZED_IMAGE_HOSTS.has(url.hostname)
    );
  } catch {
    return false;
  }
}

/**
 * Placeholder written into `tenant.logo_url` for tenants that never uploaded
 * one. It points at Decoda's own wordmark, so patient-facing surfaces have to
 * treat it as "no logo" rather than render another company's brand.
 */
export const DEFAULT_TENANT_LOGO_URL = "/logos/decoda-logo.png";

/**
 * Resolves a tenant logo for a patient-facing surface, collapsing both
 * "unset" shapes — empty string and the Decoda placeholder — to "".
 */
export function resolveTenantLogoUrl(
  logoUrl: string | undefined | null,
): string {
  if (!logoUrl || logoUrl === DEFAULT_TENANT_LOGO_URL) return "";
  return resolveImageUrl(logoUrl);
}
